import React from "react"
import { useParams } from "react-router-dom"
import {
  Button,
  IconButton,
  Table,
  Thead,
  Tbody,
  Td,
  Tr,
  TableContainer,
  Box,
  Th,
} from "@chakra-ui/react"
import { AddIcon, DeleteIcon } from "@chakra-ui/icons"
import {
  Schemas,
  DatasetSchemas,
  SchemaRecordCounts,
  CurrentOrg,
} from "../lib/store"
import { getPlanLimits } from "../lib/limits"
import Link from "./Link"
import DeleteConfirmModal from "./DeleteConfirmModal"

export default function DatasetDetail() {
  // Global state
  const [schemas] = Schemas.useState()
  const [datasetSchemas] = DatasetSchemas.useState()
  const [schemaRecordCounts] = SchemaRecordCounts.useState()
  const [currentOrg] = CurrentOrg.useState()

  // Path params
  const { datasetId } = useParams()

  // What to display
  const [schemasList, setSchemasList] = React.useState([])
  const [deleteTarget, setDeleteTarget] = React.useState(null)

  // Pick the dataset and the schemas that belong to it
  React.useEffect(() => {
    setSchemasList(
      (datasetSchemas[datasetId] || [])
        .map((id) => schemas[id])
        .filter(Boolean),
    )
  }, [datasetId, schemas, datasetSchemas])

  const schemaCount = datasetSchemas[datasetId]?.length || 0
  const { maxSchemasPerDataset } = getPlanLimits(currentOrg)
  const atSchemaLimit =
    maxSchemasPerDataset !== null && schemaCount >= maxSchemasPerDataset

  return (
    <Box display="flex" flexDir="column" height="100%" py={0} px={8} gap={4}>
      <Link
        to={atSchemaLimit ? undefined : `/datasets/${datasetId}/schemas/create`}
        ml="auto"
        style={atSchemaLimit ? { pointerEvents: "none" } : undefined}
      >
        <Button
          variant="primary"
          rightIcon={<AddIcon />}
          id="btn-schema-new"
          title={
            atSchemaLimit
              ? `Your plan supports up to ${maxSchemasPerDataset} schema${
                  maxSchemasPerDataset === 1 ? "" : "s"
                } per dataset`
              : "Create a schema"
          }
          size="sm"
          isDisabled={atSchemaLimit}
        >
          Create Schema
          {atSchemaLimit && ` (${schemaCount}/${maxSchemasPerDataset})`}
        </Button>
      </Link>
      <TableContainer
        borderRadius="lg"
        boxShadow="lg"
        overflowY="auto"
        bg="white"
        overflow="visible"
      >
        <Table size="xs">
          <Thead>
            <Tr>
              <Th>Schema</Th>
              <Th>Fields</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {schemasList.map((row, index) => (
              <Tr key={index}>
                <Td fontWeight="medium">
                  <Link
                    to={`/datasets/${datasetId}/schemas/${row.id}`}
                    variant="underline"
                    textDecoration="underline"
                  >
                    {row.name}
                  </Link>
                </Td>
                <Td>
                  {(row.schema?.properties &&
                    Object.keys(row.schema.properties).length) ||
                    "Unknown"}
                </Td>
                <Td isNumeric>
                  <IconButton
                    aria-label={`Delete schema ${row.name}`}
                    icon={<DeleteIcon />}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => setDeleteTarget(row)}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {deleteTarget && (
        <DeleteConfirmModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          type="schema"
          name={deleteTarget.name}
          id={deleteTarget.id}
          recordCount={schemaRecordCounts[deleteTarget.id] || 0}
        />
      )}
    </Box>
  )
}
